import { createHttpClient } from '@vista-digital/http-client';
import { MoviexchangeCdnClient } from './providers/moviexchange/moviexchange-cdn-client';
import { VistaCdnClient } from './providers/vista/vista-cdn-client';
import { CdnClientError } from './types/error/cdn-client-error';
import { CdnClientErrorCode } from './types/error/cdn-client-error-code';
import { getCdnMediaType } from './utils/get-cdn-media-type';
import { isCdnMediaAvailable } from './utils/is-cdn-media-available';
/**
 * A front-end client helper for consuming assets from the Vista CDN.
 */
export class CdnClient {
    /**
     * Creates a CDN Client instance.
     * @param configuration The configuration settings of the CDN client.
     */
    constructor(configuration) {
        /**
         * Creates an instance of Http Client.
         */
        this.httpClient = createHttpClient();
        const { vista, moviexchange } = configuration;
        if (!vista) {
            throw new CdnClientError(CdnClientErrorCode.VistaProviderNotConfigured);
        }
        this.vistaCdnClient = new VistaCdnClient(vista);
        if (moviexchange) {
            this.moviexchangeCdnClient = new MoviexchangeCdnClient(moviexchange);
        }
    }
    /**
     * URL builder for Film assets.
     * @param film The the Film entity.
     * @param options Options for the Film entity.
     */
    film(film, options) {
        const { moviexchangeReleaseId } = film.externalIds;
        if (this.moviexchangeCdnClient && moviexchangeReleaseId) {
            return this.moviexchangeCdnClient.film(moviexchangeReleaseId);
        }
        else {
            return this.vistaCdnClient.film(film.id, options);
        }
    }
    /**
     * URL builder for Site assets.
     * @param siteId The ID of the Site entity.
     * @param options Options for the Site entity.
     */
    site(siteId, options) {
        return this.vistaCdnClient.site(siteId, options);
    }
    /**
     * URL builder for Item assets.
     * @param itemId The ID of the Item entity.
     * @param options Options for the Item entity.
     */
    item(itemId, options) {
        return this.vistaCdnClient.item(itemId, options);
    }
    /**
     * URL builder for Ticket Type assets.
     * @param ticketTypeId The ID of the Ticket Type entity.
     * @param options Options for the Ticket Type entity.
     */
    ticketType(ticketTypeId, options) {
        return this.vistaCdnClient.ticketType(ticketTypeId, options);
    }
    /**
     * URL builder for Advertising Profile assets.
     * @param advertisingProfileId The ID of the Advertising Profile entity.
     * @param options Options for the Advertising Profile entity.
     */
    advertisingProfile(advertisingProfileId, options) {
        return this.vistaCdnClient.advertisingProfile(advertisingProfileId, options);
    }
    /**
     * URL builder for Promotion assets.
     * @param promotionId The ID of the Promotion entity.
     * @param options Options for the Promotion entity.
     */
    promotion(promotionId, options) {
        return this.vistaCdnClient.promotion(promotionId, options);
    }
    /**
     * URL builder for Attribute assets.
     * @param attributeId The ID of the Attribute entity.
     * @param options Options for the Attribute entity.
     */
    attribute(attributeId, options) {
        return this.vistaCdnClient.attribute(attributeId, options);
    }
    /**
     * URL builder for Event assets.
     * @param eventId The ID of the Event entity.
     * @param options Options for the Event entity.
     */
    event(eventId, options) {
        return this.vistaCdnClient.event(eventId, options);
    }
    /**
     * URL builder for Censor Rating assets.
     * @param censorRatingId The ID of the Censor Rating entity.
     * @param options Options for the Censor Rating entity.
     */
    censorRating(censorRatingId, options) {
        return this.vistaCdnClient.censorRating(censorRatingId, options);
    }
    /**
     * URL builder for Corporate Booking assets.
     * @param corporateBookingId The ID of the Corporate Booking entity.
     * @param options Options for the Corporate Booking entity.
     */
    corporateBooking(corporateBookingId, options) {
        return this.vistaCdnClient.corporateBooking(corporateBookingId, options);
    }
    /**
     * URL builder for Loyalty Club assets.
     * @param loyaltyClubId The ID of the Loyalty Club entity.
     * @param options Options for the Loyalty Club entity.
     */
    loyaltyClub(loyaltyClubId, options) {
        return this.vistaCdnClient.loyaltyClub(loyaltyClubId, options);
    }
    /**
     * URL builder for Loyalty Membership Program assets.
     * @param loyaltyMembershipProgramId The ID of the Loyalty Membership Program entity.
     * @param options Options for the Loyalty Membership Program entity.
     */
    loyaltyMembershipProgram(loyaltyMembershipProgramId, options) {
        return this.vistaCdnClient.loyaltyMembershipProgram(loyaltyMembershipProgramId, options);
    }
    /**
     * URL builder for Loyalty Reward Shop Item assets.
     * @param loyaltyRewardShopItemId The ID of the Loyalty Reward Shop Item entity.
     * @param options Options for the Loyalty Reward Shop Item entity.
     */
    loyaltyRewardShopItem(loyaltyRewardShopItemId, options) {
        return this.vistaCdnClient.loyaltyRewardShopItem(loyaltyRewardShopItemId, options);
    }
    /**
     * URL builder for Web Payment Method assets.
     * @param webPaymentMethodId The ID of the Web Payment Method entity.
     * @param options Options for the Web Payment Method entity.
     */
    webPaymentMethod(webPaymentMethodId, options) {
        return this.vistaCdnClient.webPaymentMethod(webPaymentMethodId, options);
    }
    /**
     * URL builder for Loyalty Reward assets.
     * @param loyaltyRewardId The ID of the Loyalty Reward entity.
     * @param options Options for the Loyalty Reward entity.
     */
    loyaltyReward(loyaltyRewardId, options) {
        return this.vistaCdnClient.loyaltyReward(loyaltyRewardId, options);
    }
    /**
     * URL builder for Loyalty Subscription assets.
     * @param loyaltySubscriptionId The ID of the Loyalty Subscription entity.
     * @param options Options for the Loyalty Subscription's assets.
     */
    loyaltySubscription(loyaltySubscriptionId, options) {
        return this.vistaCdnClient.loyaltySubscription(loyaltySubscriptionId, options);
    }
    /**
     * Util to check if CDN media is available.
     * @param mediaUrl Media URL for asset.
     */
    async isCdnMediaAvailable(mediaUrl) {
        if (!mediaUrl) {
            return false;
        }
        return isCdnMediaAvailable(this.httpClient, mediaUrl);
    }
    /**
     * Util that returns the media type.
     * @param mediaUrl Media URL for asset.
     */
    async getCdnMediaType(mediaUrl) {
        if (!mediaUrl) {
            return undefined;
        }
        return getCdnMediaType(this.httpClient, mediaUrl);
    }
}
//# sourceMappingURL=cdn-client.js.map